import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import DashboardHeader from "@/components/DashboardHeader";
import BackButton from "@/components/BackButton";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  ArrowRight,
  BookOpen,
  CheckCircle2,
  ClipboardList,
  ClipboardCheck,
  Loader2,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import {
  ageBucketClass,
  ageBucketLabel,
  generationStatusClass,
  generationStatusLabel,
  getLearningPath,
  sensitiveBucketLabel,
} from "@/lib/learningPaths";
import { startTest } from "@/lib/grading";

/**
 * Phase 15b — Self-paced course reader.
 *
 * Layout:
 * - Left rail: chapter list with click-to-select.
 * - Main pane: three tabs per chapter (Read · Practice · Test).
 * - Persistent "Take chapter test" CTA calls /test/start and routes to TestRunner.
 */

function Sidebar({ chapters, activeIdx, onPick }) {
  return (
    <nav
      className="w-full sm:w-64 shrink-0 border-r border-stone-200 bg-white/60 sm:min-h-[600px]"
      data-testid="chapter-sidebar"
    >
      <div className="p-4 text-xs font-semibold uppercase tracking-wide text-stone-500 border-b border-stone-100">
        Chapters ({chapters.length})
      </div>
      <ul>
        {chapters.map((ch, i) => (
          <li key={ch.id}>
            <button
              type="button"
              onClick={() => onPick(i)}
              className={`w-full text-left px-4 py-3 border-b border-stone-100 text-sm flex items-start gap-2 hover:bg-amber-50 transition ${
                i === activeIdx ? "bg-amber-50 border-l-2 border-l-amber-500" : ""
              }`}
              data-testid={`chapter-nav-${i}`}
            >
              <span className="text-xs font-mono text-stone-400 mt-0.5 shrink-0">
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className="flex-1">{ch.title}</span>
            </button>
          </li>
        ))}
      </ul>
    </nav>
  );
}

function LessonView({ chapter }) {
  const cw = chapter.classwork || {};
  return (
    <div className="space-y-6" data-testid="lesson-view">
      {chapter.learning_objectives?.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-amber-800 mb-2">
            You'll learn to…
          </div>
          <ul className="list-disc pl-5 space-y-1 text-sm text-stone-800">
            {chapter.learning_objectives.map((o, i) => (
              <li key={i}>{o}</li>
            ))}
          </ul>
        </div>
      )}

      <article
        className="prose prose-stone prose-sm max-w-none prose-headings:font-semibold prose-h1:text-2xl prose-h2:text-xl prose-h3:text-lg prose-a:text-amber-700 prose-strong:text-stone-900"
        data-testid="lesson-notes-markdown"
      >
        <ReactMarkdown>
          {cw.lesson_notes_markdown || "*(No lesson notes yet.)*"}
        </ReactMarkdown>
      </article>

      {cw.examples?.length > 0 && (
        <Card className="border-stone-200">
          <CardHeader>
            <CardTitle className="text-xs font-semibold uppercase tracking-wide text-stone-600">
              Worked examples
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            {cw.examples.map((e, i) => (
              <div
                key={i}
                className="p-3 rounded-md bg-stone-50 border border-stone-200"
                data-testid={`example-${i}`}
              >
                {e}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {cw.teacher_talking_points?.length > 0 && (
        <Card className="border-stone-200">
          <CardHeader>
            <CardTitle className="text-xs font-semibold uppercase tracking-wide text-stone-600">
              Self-study tips
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm">
            <ul className="list-disc pl-5 space-y-1 text-stone-800">
              {cw.teacher_talking_points.map((p, i) => (
                <li key={i}>{p}</li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function HomeworkView({ chapter }) {
  const hw = chapter.homework || {};
  const questions = hw.questions || [];
  return (
    <div className="space-y-4" data-testid="homework-view">
      {hw.instructions && (
        <Card className="border-stone-200 bg-stone-50/60">
          <CardContent className="py-3 text-sm text-stone-700">
            {hw.instructions}
          </CardContent>
        </Card>
      )}
      {questions.length === 0 ? (
        <div className="text-sm text-stone-500">No homework for this chapter.</div>
      ) : (
        <ol className="list-decimal pl-6 space-y-3 text-sm">
          {questions.map((q, i) => (
            <li key={q.id || i} data-testid={`homework-q-${i}`}>
              <div className="font-medium">{q.question}</div>
              {q.expected_points?.length > 0 && (
                <details className="mt-1 text-xs text-stone-600">
                  <summary className="cursor-pointer hover:text-stone-900">
                    Hints
                  </summary>
                  <ul className="list-disc pl-5 mt-1 space-y-0.5">
                    {q.expected_points.map((pt, k) => (
                      <li key={k}>{pt}</li>
                    ))}
                  </ul>
                </details>
              )}
            </li>
          ))}
        </ol>
      )}
      <div className="text-xs text-stone-500 pt-1">
        Self-paced homework is for practice — jot your answers on paper, then jump
        into the chapter test when you're ready.
      </div>
    </div>
  );
}

function TestSummary({ chapter, onStart, starting }) {
  const test = chapter.test || {};
  const mcqCount = (test.mcq || []).length;
  const subjCount = (test.subjective || []).length;
  return (
    <div className="space-y-4" data-testid="test-summary">
      <Card className="border-amber-200 bg-amber-50/40">
        <CardContent className="py-4 space-y-2">
          <div className="flex items-center gap-2 text-amber-900 font-semibold">
            <ClipboardCheck className="h-4 w-4" /> Chapter test
          </div>
          <div className="text-sm text-stone-700">
            {test.duration_minutes || 20} minutes · {mcqCount} MCQs
            {subjCount > 0 && ` · ${subjCount} short-answer`}
          </div>
          <div className="text-xs text-stone-500">
            Written answers are graded instantly by our AI grader — you'll see
            feedback per question the moment you submit.
          </div>
          <Button
            className="bg-amber-600 hover:bg-amber-700 text-white mt-2"
            onClick={onStart}
            disabled={starting}
            data-testid="start-test-button"
          >
            {starting ? (
              <>
                <Loader2 className="mr-1 h-4 w-4 animate-spin" /> Starting…
              </>
            ) : (
              <>
                Start the test <ArrowRight className="ml-1 h-4 w-4" />
              </>
            )}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

export default function StudentLearnCourse() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeIdx, setActiveIdx] = useState(0);
  const [starting, setStarting] = useState(false);

  const reload = useCallback(() => {
    setLoading(true);
    getLearningPath(courseId)
      .then(setCourse)
      .catch(() => {
        toast.error("Path not found or you don't have access.");
        navigate("/student/learn");
      })
      .finally(() => setLoading(false));
  }, [courseId, navigate]);

  useEffect(() => {
    reload();
  }, [reload]);

  // Poll while still generating.
  useEffect(() => {
    if (!course?.ai_generation) return;
    const s = course.ai_generation.status;
    if (s !== "queued" && s !== "running") return;
    const t = setInterval(reload, 4000);
    return () => clearInterval(t);
  }, [course, reload]);

  const chapter = useMemo(
    () => course?.chapters?.[activeIdx],
    [course, activeIdx]
  );

  const handleStartTest = async () => {
    if (!chapter) return;
    setStarting(true);
    try {
      const attempt = await startTest(courseId, chapter.id);
      navigate(`/student/test-attempts/${attempt.attempt_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start test");
    } finally {
      setStarting(false);
    }
  };

  if (loading || !course) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center text-stone-500">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  const gen = course.ai_generation;
  const isGenerating =
    gen && (gen.status === "queued" || gen.status === "running");
  const chapters = course.chapters || [];

  return (
    <div
      className="min-h-screen bg-stone-50 text-stone-900"
      data-testid="student-learn-course"
    >
      <DashboardHeader subtitle="Student · Self-paced learning" />
      <main className="mx-auto max-w-6xl px-6 py-8 space-y-4">
        <BackButton
          to="/student/learn"
          label="My learning paths"
          testId="back-to-hub"
        />

        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 text-amber-700 mb-1">
              <Sparkles className="h-4 w-4" />
              <span className="text-xs font-semibold uppercase tracking-wide">
                Self-paced learning path
              </span>
            </div>
            <h1
              className="text-2xl font-semibold tracking-tight"
              data-testid="course-title"
            >
              {course.title}
            </h1>
            {course.subtitle && (
              <p className="text-sm text-stone-600 mt-1">{course.subtitle}</p>
            )}
            <div className="flex flex-wrap items-center gap-1.5 mt-3">
              <Badge
                variant="outline"
                className={ageBucketClass(course.learning_profile?.age_bucket)}
              >
                {ageBucketLabel(course.learning_profile?.age_bucket)}
              </Badge>
              <Badge
                variant="outline"
                className="bg-stone-50 text-stone-700 border-stone-200"
              >
                {course.level}
              </Badge>
              {gen && (
                <Badge
                  variant="outline"
                  className={generationStatusClass(gen.status)}
                  data-testid="course-gen-status"
                >
                  {generationStatusLabel(gen.status)}
                </Badge>
              )}
              {course.learning_profile?.sensitive_bucket && (
                <Badge
                  variant="outline"
                  className="bg-blue-50 text-blue-800 border-blue-200"
                >
                  {sensitiveBucketLabel(
                    course.learning_profile.sensitive_bucket
                  )}
                </Badge>
              )}
            </div>
          </div>
        </div>

        {isGenerating && (
          <Card className="border-amber-200 bg-amber-50/60">
            <CardContent className="py-3 text-sm text-amber-900 flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Drafting your chapters ({gen.progress || 0}%) — usually about 30-60
              seconds. This page will refresh automatically.
            </CardContent>
          </Card>
        )}

        {gen?.status === "failed" && (
          <Card className="border-red-200 bg-red-50/60">
            <CardContent className="py-3 text-sm text-red-800">
              Generation failed. Please delete this path and try a slightly
              different topic.
            </CardContent>
          </Card>
        )}

        {chapters.length > 0 && (
          <Card className="border-stone-200 overflow-hidden">
            <div className="flex flex-col sm:flex-row">
              <Sidebar
                chapters={chapters}
                activeIdx={activeIdx}
                onPick={setActiveIdx}
              />
              <div className="flex-1 min-w-0 p-5 sm:p-6 space-y-4">
                <div className="space-y-1">
                  <div className="text-xs text-stone-400 font-mono">
                    Chapter {activeIdx + 1} of {chapters.length}
                  </div>
                  <h2
                    className="text-xl font-semibold tracking-tight"
                    data-testid="active-chapter-title"
                  >
                    {chapter.title}
                  </h2>
                </div>

                <Tabs defaultValue="read" className="w-full">
                  <TabsList data-testid="chapter-tabs">
                    <TabsTrigger value="read" data-testid="tab-read">
                      <BookOpen className="h-3.5 w-3.5 mr-1" /> Read
                    </TabsTrigger>
                    <TabsTrigger value="homework" data-testid="tab-homework">
                      <ClipboardList className="h-3.5 w-3.5 mr-1" /> Practice
                    </TabsTrigger>
                    <TabsTrigger value="test" data-testid="tab-test">
                      <ClipboardCheck className="h-3.5 w-3.5 mr-1" /> Test
                    </TabsTrigger>
                  </TabsList>
                  <TabsContent value="read" className="pt-4">
                    <LessonView chapter={chapter} />
                  </TabsContent>
                  <TabsContent value="homework" className="pt-4">
                    <HomeworkView chapter={chapter} />
                  </TabsContent>
                  <TabsContent value="test" className="pt-4">
                    <TestSummary
                      chapter={chapter}
                      onStart={handleStartTest}
                      starting={starting}
                    />
                  </TabsContent>
                </Tabs>

                {activeIdx < chapters.length - 1 && (
                  <div className="pt-4 border-t border-stone-100 flex justify-end">
                    <Button
                      variant="outline"
                      onClick={() => setActiveIdx(activeIdx + 1)}
                      data-testid="next-chapter-button"
                    >
                      Next chapter{" "}
                      <ArrowRight className="ml-1 h-3.5 w-3.5" />
                    </Button>
                  </div>
                )}
                {activeIdx === chapters.length - 1 && (
                  <div className="pt-4 border-t border-stone-100 text-center text-sm text-stone-600">
                    <CheckCircle2 className="h-4 w-4 inline text-emerald-600 mr-1" />
                    You've reached the last chapter. Well done!
                  </div>
                )}
              </div>
            </div>
          </Card>
        )}
      </main>
    </div>
  );
}
